// Requires `source_ref` in RESOLVE_RELATIONS to inline the referenced content.

import type { SbBlokData } from "@storyblok/react/rsc";

export type SourcedBlok<T> = T & {
  source_ref?: string | { content?: T } | null;
};

// Renderers use `src ?? blok` per field.
export function pickSourceContent<T>(blok: SourcedBlok<T>): T | null {
  const ref = blok.source_ref;
  if (!ref || typeof ref !== "object") return null;
  const content = (ref as { content?: T }).content;
  return content ?? null;
}

export function isPresent(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "boolean") return value === true;
  if (typeof value === "number") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if ("filename" in o) return Boolean(o.filename); // asset
    if ("linktype" in o || "cached_url" in o || "url" in o) {
      return Boolean(o.id || o.url || o.cached_url || o.email || o.story); // link
    }
    return Object.keys(o).length > 0;
  }
  return true;
}

// The instance value wins when the editor set it; otherwise the source's.
export function override<T>(instanceValue: T, sourceValue: T | undefined): T {
  return isPresent(instanceValue) ? instanceValue : (sourceValue as T);
}

export function mergeSourced<T extends Record<string, unknown>>(
  blok: SbBlokData & SourcedBlok<T>,
  keys: (keyof T)[],
): SbBlokData & T {
  const src = pickSourceContent<T>(blok);
  const out = { ...blok } as SbBlokData & T;
  if (src) {
    for (const key of keys) {
      (out as T)[key] = override((out as T)[key], src[key]);
    }
  }
  return out;
}
